import { MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';

import { avatarColorClass, getInitials } from './helpers';
import type { Customer } from './types';

interface Props {
  customer: Customer;
  index: number;
  onSelect: (customerId: string) => void;
}

export function CustomerCard({ customer, index, onSelect }: Props) {
  const { t } = useLanguage();

  const isActive = customer.status === 'active';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(customer.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(customer.id);
      }}
      className="bg-card border rounded-xl p-5 flex flex-col gap-4 hover:shadow-sm transition-shadow cursor-pointer"
      data-testid={`customer-card-${customer.id}`}
    >
      {/* Avatar + name row */}
      <div className="flex items-center gap-3">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${avatarColorClass(index)}`}
        >
          {getInitials(customer.full_name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{customer.full_name}</p>
        </div>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${
            isActive
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {isActive ? t.customerActive : t.customerInactive}
        </span>
      </div>

      {/* Contact */}
      <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
        {customer.phone && (
          <div className="flex items-center gap-2">
            <PhoneIcon className="w-3.5 h-3.5 shrink-0" />
            <span>{customer.phone}</span>
          </div>
        )}
        {customer.email && (
          <div className="flex items-center gap-2">
            <MailIcon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{customer.email}</span>
          </div>
        )}
        {customer.service_address && (
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{customer.service_address}</span>
          </div>
        )}
      </div>
    </div>
  );
}
